import Link from 'next/link';
import { PlanFeatureItemProps } from '../../types/components/pricing/PlanFeatureItem';

import { CheckmarkIcon, QuestionIcon, ExternalLinkIcon } from '../../modules/Icons';
import { Tooltip } from 'react-tooltip';
import LicenseSelectionAssistDialog from '../shared/LicenseSelectionAssistDialog';
import EssentialsCategoryModalWrapper from '../collections/EssentialsCategoryModalWrapper';

import markdownHelpers from '../../helpers/markdown';
import { useState } from 'react';

const PlanFeatureItem: React.FC<PlanFeatureItemProps> = ({
  feature,
  interval,
  selectedPlanVariant,
  licenseId,
}) => {
  if (!feature || !interval) return <></>;

  const isEssentialsCatalogFeatureItem = (): boolean => {
    return feature.feature_text_item.toLowerCase().includes('essentials');
  }

  const isCreditsPerIntervalItem = (): boolean => {
    return feature.feature_text_item.toLowerCase().includes('5 credits');
  }

  if (selectedPlanVariant?.recurring_credits === 0 && isCreditsPerIntervalItem()) return <></>

  const [licenseSelectionAssistDialogOpen, setLicenseSelectionAssistDialogOpen] = useState<boolean>(false);
  const [essentialsCatalogModalOpen, setEssentialsCatalogModalOpen] = useState<boolean>(false);

  const { convertMarkdownToHtml } = markdownHelpers;

  const handleQuestionIconClick = (feature_text_item: string) => {
    if (feature_text_item.toLowerCase().includes('licensed') || feature_text_item.toLowerCase().includes('license'))
      setLicenseSelectionAssistDialogOpen(true);
  }

  const determineIconStyles = (feature_text_item: string) => {
    const lowercaseText = feature_text_item.toLowerCase();
    return lowercaseText.includes('licensed') || lowercaseText.includes('license') || isEssentialsCatalogFeatureItem()
      ? 'hover:stroke-[#00a1e0]'
      : '';
  };

  const dynamicFeatures = (feature_text_item: string) => {
    return feature_text_item
      .replace('5 credits', `${selectedPlanVariant?.recurring_credits} credits`)
      .replace('per month', `per ${interval}`);
  };

  const tooltipId = `${feature.feature_text_item}-${licenseId}`;

  const specialTooltipText = (feature_text_item: string) => {
    if (feature_text_item.toLowerCase().includes("actionvfx sampler pack")) {
      return <>{feature.tooltip_text} <Link href="/collections/the-actionvfx-sampler-pack-stock-footage" className='whitespace-nowrap text-[#24C1FF]'>See Sampler Pack</Link></>
    }
    return feature.tooltip_text
  }

  return (
    <>
      <li className="flex items-start gap-2" cy-test-id={`plan-feature-item-${feature._uid}`}>
        <CheckmarkIcon className="flex-shrink-0 mt-1" />
        <div className="relative flex-1">
          <span
            cy-test-id="feature-text"
            dangerouslySetInnerHTML={{
              __html: convertMarkdownToHtml(dynamicFeatures(feature.feature_text_item,)),
            }}
          />
          {feature.show_tooltip_icon && !isEssentialsCatalogFeatureItem() && (
          <>
            <QuestionIcon
              data-tooltip-id={tooltipId}
              className={`${determineIconStyles(feature.feature_text_item)} inline-block ml-1 align-text-bottom scale-90 cursor-pointer md:scale-100 question-icon`}
              onClick={() => handleQuestionIconClick(feature.feature_text_item)}
              cy-test-id="tooltip-icon"
            />
            <Tooltip
              id={tooltipId}
              className="max-w-[300px] z-50 !text-[14px] !normal-case leading-[22px] rounded-[5px] py-[10px] shadow tooltip"
              opacity={1}
              clickable={true}
              style={{ backgroundColor: 'black' }}>
                {specialTooltipText(feature.feature_text_item)}
            </Tooltip>
          </>
          )}
          {isEssentialsCatalogFeatureItem() && (
            <div
              className="inline-flex items-center justify-start gap-1 ml-2 cursor-pointer"
              onClick={() => setEssentialsCatalogModalOpen(true)}
              cy-test-id="essentials-catalog-link"
            >
              <div className="font-normal leading-snug text-a-blue inter text-12">Preview Catalog</div>
              <ExternalLinkIcon/>
            </div>
          )}
        </div>
      </li>
      <LicenseSelectionAssistDialog
        dialogIsOpen={licenseSelectionAssistDialogOpen}
        setDialogIsOpen={setLicenseSelectionAssistDialogOpen}
      />
      <EssentialsCategoryModalWrapper
        isOpen={essentialsCatalogModalOpen}
        setIsOpen={setEssentialsCatalogModalOpen}
      />
    </>
  )
}

export default PlanFeatureItem;
